import Add from '@mui/icons-material/Add';
import Delete from '@mui/icons-material/Delete';
import Edit from '@mui/icons-material/Edit';
import MoreHoriz from '@mui/icons-material/MoreHoriz';
import WarningAmber from '@mui/icons-material/WarningAmber';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import {
  EquipmentType_Create,
  ModelError_Item,
  OrderUnitOfMeasure,
  patchV1CompaniesCompanyIdEquipmentTypesId,
  postV1CompaniesCompanyIdEquipmentTypes,
} from '@treadinc/horizon-api-spec';
import axios from 'axios';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { CSVImportExportButton } from '~components/CSVImportExport/CSVImportExportButton';
import { CSVImportExportModal } from '~components/CSVImportExport/CSVImportExportModal';
import { useCSVModal } from '~components/CSVImportExport/useCSVModal';
import DataGridPager from '~components/DataGrid/DataGridPager';
import actionsColumn from '~components/DataTable/actionsColumn';
import DataTable from '~components/DataTable/DataTable';
import { DataTableColumn } from '~components/DataTable/dataTableColumn';
import ConfirmationDialog from '~components/Dialog/ConfirmationDialog';
import Dialog from '~components/Dialog/Dialog';
import DialogActions from '~components/Dialog/DialogActions';
import DialogContent from '~components/Dialog/DialogContent';
import DialogHeader from '~components/Dialog/DialogHeader';
import { ShakyErrorIndicator } from '~components/ErrorIndicators/ShakyErrorIndicator';
import { SimpleMenu } from '~components/Menu/SimpleMenu';
import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import { MAX_API_PAGE_SIZE } from '~constants/consts';
import { useAllowedUnitOfMeasureOptions } from '~hooks/useAllowedUnitOfMeasureOptions';
import { createEquipmentTypesConfig } from '~hooks/useCSVImportExport/configs/equipmentTypesConfig';
import {
  createNameResolver,
  requireResolvedId,
} from '~hooks/useCSVImportExport/useEntityCsvConfig';
import useCursorPagination from '~hooks/useCursorPagination/useCursorPagination';
import { EquipmentTypeItem } from '~hooks/useEquipment/models';
import useEquipment from '~hooks/useEquipment/useEquipment';
import { MaterialTypeahead } from '~hooks/useMaterials/models';
import SettingsPageHeader from '~pages/Settings/components/SettingsPageHeader';
import useCsvImportLookupsQuery, {
  CsvImportLookupName,
} from '~queries/useCsvImportLookupsQuery';
import connection from '~services/connectionModule';
import { Paginated } from '~services/pagination';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { dateFormat } from '~utils/dateTime';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';
import { nullableStringOrNumberIsValidNumber } from '~utils/utilFunctions';

import EquipmentTypeForm, { EquipmentTypeFormHandler } from './EquipmentTypeForm';
import { EquipmentTypeDto, EquipmentTypeDtoAsyncError } from './equipmentTypeSchema';

const MATERIALS_LOOKUP: CsvImportLookupName[] = ['materials'];

const NO_COMPANY_PAGE: Paginated<EquipmentTypeItem> = {
  data: [],
  pagination: { limit: MAX_API_PAGE_SIZE, page: 1 },
};

const EquipmentTypeManagement = observer(() => {
  const { userStore, toasterStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);

  // EquipmentType APIs
  const { getEquipmentTypesByCompanyId, deleteEquipmentType } = useEquipment();
  const [isDeletingEquipmentType, setIsDeletingEquipmentType] = useState(false);

  const {
    goToPage: goToEquipmentTypesPage,
    isFetching,
    pagination,
    reloadCurrentPage: reloadEquipmentTypesPage,
    rows,
    startOver: startEquipmentTypesOver,
  } = useCursorPagination<EquipmentTypeItem>({
    fetchPage: ({ cursor, limit, link, signal }) => {
      if (!companyId) {
        return Promise.resolve(NO_COMPANY_PAGE);
      }

      return getEquipmentTypesByCompanyId({ companyId, cursor, limit, link, signal });
    },
    limit: MAX_API_PAGE_SIZE,
  });

  useEffect(() => {
    void startEquipmentTypesOver();
  }, [companyId, startEquipmentTypesOver]);

  // Get user permissions to determine if user can create/edit/delete equipment types
  const userPermissions = userStore.getPermissions();

  // Modal Actions
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const [formDialogAsyncErrors, setFormDialogAsyncErrors] = useState<
    EquipmentTypeDtoAsyncError[]
  >([]);
  const [formDialogState, setFormDialogState] = useState<{
    equipmentType?: EquipmentTypeItem;
    isOpen: boolean;
    isSaving: boolean;
  }>({ isOpen: false, isSaving: false });

  const formDialogRef = useRef<EquipmentTypeFormHandler>(null);

  const unitOfMeasureOptions = useAllowedUnitOfMeasureOptions();

  const getUomByName = useCallback(
    (name: string): string | undefined => {
      const uom = unitOfMeasureOptions.find(
        (u) => u.name.toLowerCase() === name.toLowerCase(),
      );
      return uom?.id;
    },
    [unitOfMeasureOptions],
  );

  const { loadLookups: loadMaterialLookups, lookups: materialLookups } =
    useCsvImportLookupsQuery({
      companyId: companyId || '',
      isEnabled: false,
      lookupNames: MATERIALS_LOOKUP,
    });

  const createEquipmentTypesConfigWithMaterials = useCallback(
    (materials: MaterialTypeahead[], materialsLoadError?: string) =>
      createEquipmentTypesConfig(
        getUomByName,
        requireResolvedId(createNameResolver(materials, materialsLoadError), 'materials'),
        companyId,
      ),
    [companyId, getUomByName],
  );

  const equipmentTypesConfig = useMemo(
    () => createEquipmentTypesConfigWithMaterials(materialLookups.materials),
    [createEquipmentTypesConfigWithMaterials, materialLookups.materials],
  );

  const loadEquipmentTypesConfig = useCallback(async () => {
    const { errors, lookups } = await loadMaterialLookups();

    return createEquipmentTypesConfigWithMaterials(lookups.materials, errors.materials);
  }, [createEquipmentTypesConfigWithMaterials, loadMaterialLookups]);

  const csvModal = useCSVModal({
    onImportComplete: () => void goToEquipmentTypesPage(),
  });

  const handleEditRow = useCallback(
    (equipmentTypeId: string) => {
      const equipmentType = rows.find(({ id }) => id === equipmentTypeId);

      if (!equipmentType) {
        return;
      }

      setFormDialogState({ isSaving: false, isOpen: true, equipmentType });
    },
    [rows],
  );

  const deleteRow = (id: string) => {
    setSelectedId(id);
    setIsDeleteDialogOpen(true);
  };

  const deleteEquipmentTypeCallback = () => {
    if (selectedId) {
      setIsDeletingEquipmentType(true);
      deleteEquipmentType({ equipmentTypeId: selectedId })
        .then(() => {
          void reloadEquipmentTypesPage();
          setIsDeleteDialogOpen(false);
          setSelectedId(null);
          toasterStore.push(
            alert(t('administration.equipment_type.deleted_message'), AlertTypes.success),
          );
        })
        .catch(() => {
          // Error is already handled in the hook
        })
        .finally(() => {
          setIsDeletingEquipmentType(false);
        });
    }
  };

  const closeDeleteDialog = () => {
    setIsDeleteDialogOpen(false);
    setSelectedId(null);
  };

  const handleCloseFormDialog = useCallback(() => {
    setFormDialogState((state) => ({ ...state, isOpen: false }));
  }, []);

  const handleSubmitFormDialog = useCallback(async () => {
    const formData = await formDialogRef.current?.onSubmit();

    if (!formData) {
      return;
    }

    const equipmentTypeId = formDialogState.equipmentType?.id;

    try {
      setFormDialogAsyncErrors([]);
      setFormDialogState((state) => ({ ...state, isSaving: true }));

      const body: EquipmentType_Create = {
        name: formData.name,
        external_id: formData.externalId || undefined,
        gross_capacity: nullableStringOrNumberIsValidNumber(
          formData.grossCapacity ?? null,
        )
          ? Number(formData.grossCapacity)
          : undefined,
        gross_capacity_unit_of_measure: (formData.grossCapacityUnitOfMeasure?.value ||
          undefined) as OrderUnitOfMeasure,
        accounting_id: formData.accountingId || undefined,
        driver_classification: formData.driverClassification?.value ?? null,
        additional_gross_capacities: formData.additionalGrossCapacities.map((field) => ({
          capacity: field.capacity.toFixed(2),
          unit_of_measure: field.unitOfMeasure.value as OrderUnitOfMeasure,
        })),
        material_ids: formData.materials.map((material) => material.value),
      };

      if (equipmentTypeId) {
        const response = await patchV1CompaniesCompanyIdEquipmentTypesId({
          path: {
            'company-id': companyId,
            id: equipmentTypeId,
          },
          body,
        });
        const parsed = EquipmentTypeItem.parse(response.data.data);

        void reloadEquipmentTypesPage();
        toasterStore.push(
          alert(
            t('administration.equipment_type.updated_message', { name: parsed.name }),
            AlertTypes.success,
          ),
        );
        setFormDialogState({ isSaving: false, isOpen: false });
      } else {
        const response = await postV1CompaniesCompanyIdEquipmentTypes({
          path: { 'company-id': formData.company.value },
          body,
        });
        const parsed = EquipmentTypeItem.parse(response.data.data);

        void goToEquipmentTypesPage();
        toasterStore.push(
          alert(
            t('administration.equipment_type.created_message', { name: parsed.name }),
            AlertTypes.success,
          ),
        );
        setFormDialogState({ isSaving: false, isOpen: false });
      }
    } catch (error) {
      setFormDialogState((state) => ({ ...state, isSaving: false }));

      if (axios.isAxiosError(error)) {
        const errorsArray = (error.response?.data?.error?.errors ??
          []) as ModelError_Item[];

        const asyncErrors: EquipmentTypeDtoAsyncError[] = [];
        const formFieldInfoByFieldInError: Record<
          string,
          { fieldInForm: keyof EquipmentTypeDto; label: string }
        > = {
          name: { fieldInForm: 'name', label: t('form_fields.name') },
          external_id: { fieldInForm: 'externalId', label: t('form_fields.external_id') },
        };

        errorsArray.forEach((errorsArrayItem) => {
          const { field, message } = errorsArrayItem;

          const isValueTakenError = message.includes('has already been taken');
          const fieldInfo = formFieldInfoByFieldInError[field];

          if (isValueTakenError && fieldInfo) {
            asyncErrors.push({
              field: fieldInfo.fieldInForm,
              message: t('error_messages.field_taken', { field: fieldInfo.label }),
            });
          }
        });

        if (asyncErrors.length) {
          setFormDialogAsyncErrors(asyncErrors);
        } else {
          connection.handleRequestError(
            error,
            equipmentTypeId
              ? t('error_messages.equipment_types.failed_to_update')
              : t('error_messages.equipment_types.failed_to_create'),
          );
        }
      } else {
        connection.handleRequestError(
          error,
          equipmentTypeId
            ? t('error_messages.equipment_types.failed_to_update')
            : t('error_messages.equipment_types.failed_to_create'),
        );
      }
    }
  }, [
    formDialogState.equipmentType?.id,
    goToEquipmentTypesPage,
    reloadEquipmentTypesPage,
    companyId,
  ]);

  useEffect(() => {
    if (!formDialogState.isOpen) {
      setFormDialogAsyncErrors([]);
    }
  }, [formDialogState.isOpen]);

  const columns = useMemo<Array<DataTableColumn<EquipmentTypeItem>>>(
    () => [
      {
        field: 'name',
        headerName: t('form_fields.name'),
        valueGetter: (equipmentType) => equipmentType.name,
      },
      {
        field: 'grossCapacity',
        headerName: t('form_fields.gross_capacity'),
        renderCell: (equipmentType) => (
          <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
            <Typography sx={{ fontSize: '12px' }}>
              {equipmentType.grossCapacity ? Number(equipmentType.grossCapacity) : null}{' '}
              {equipmentType.grossCapacityUnitOfMeasure?.name}
            </Typography>
            {equipmentType.isCapacityMissing && (
              <Tooltip
                title={t(
                  'administration.equipment_type.gross_capacity_needed_for_route_optimization',
                )}
              >
                <WarningAmber
                  aria-label={t(
                    'administration.equipment_type.gross_capacity_needed_for_route_optimization',
                  )}
                  fontSize="small"
                  sx={{ color: 'warning.main' }}
                />
              </Tooltip>
            )}
          </Box>
        ),
      },
      {
        field: 'externalId',
        headerName: t('form_fields.external_id'),
        valueGetter: (equipmentType) => equipmentType.externalId,
      },
      {
        field: 'createdAt',
        headerName: t('common.created_on'),
        valueGetter: (equipmentType) => dateFormat(equipmentType.createdAt),
      },
      {
        field: 'source',
        headerName: t('form_fields.source'),
        valueGetter: (equipmentType) => equipmentType.source,
      },
      actionsColumn((equipmentType) => {
        const options =
          userPermissions.canDeleteEquipmentType && !equipmentType.system
            ? [
                {
                  title: t('actions.delete'),
                  icon: <Delete />,
                  callBack: () => deleteRow(equipmentType.id),
                },
              ]
            : [];
        return (
          <>
            {userPermissions.canEditEquipmentType && !equipmentType.system && (
              <IconButton size="small" onClick={() => handleEditRow(equipmentType.id)}>
                <Edit />
              </IconButton>
            )}

            {options && (
              <SimpleMenu options={options}>
                <MoreHoriz />
              </SimpleMenu>
            )}
          </>
        );
      }),
    ],
    [rows],
  );

  return (
    <>
      <SettingsPageHeader
        title={t('administration.equipment_type.title')}
        description={t('settings.page_descriptions.equipment_types')}
      />
      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mb: 2 }}>
        <CSVImportExportButton onClick={csvModal.open} />
        <SmallButton
          color="brandV2Yellow"
          disabled={!userPermissions.canCreateEquipmentType}
          onClick={() => setFormDialogState({ isOpen: true, isSaving: false })}
          startIcon={<Add />}
        >
          {t('administration.equipment_type.create_title')}
        </SmallButton>
      </Box>
      <Box>
        <DataTable
          id="equipment-types-data-grid"
          columns={columns}
          rows={rows}
          loading={isFetching}
          headerTrailingComponent={
            <DataGridPager
              isFetching={isFetching}
              onNavigate={(link) => void goToEquipmentTypesPage(link)}
              pagination={pagination}
            />
          }
        />
      </Box>
      <Dialog
        isOpen={formDialogState.isOpen}
        preventCloseOnBackdropClick
        preventCloseOnEscapeKeyDown
      >
        <DialogHeader onClose={handleCloseFormDialog}>
          {formDialogState.equipmentType
            ? t('administration.equipment_type.update_title')
            : t('administration.equipment_type.create_title')}
        </DialogHeader>

        <DialogContent>
          <EquipmentTypeForm
            ref={formDialogRef}
            asyncErrors={formDialogAsyncErrors}
            equipmentType={formDialogState.equipmentType}
          />
        </DialogContent>

        <DialogActions>
          {Boolean(formDialogAsyncErrors.length) && (
            <ShakyErrorIndicator
              message={t('administration.users.user_form.invalid_data_error_messages')}
            />
          )}

          <SmallButton
            color="secondary"
            disabled={formDialogState.isSaving}
            onClick={handleCloseFormDialog}
            variant="outlined"
          >
            {t('actions.cancel')}
          </SmallButton>

          <SmallLoadingButton
            loading={formDialogState.isSaving}
            onClick={handleSubmitFormDialog}
          >
            {formDialogState.equipmentType ? t('actions.update') : t('actions.create')}
          </SmallLoadingButton>
        </DialogActions>
      </Dialog>
      <ConfirmationDialog
        isBusy={isDeletingEquipmentType}
        isOpen={isDeleteDialogOpen}
        onClose={closeDeleteDialog}
        onConfirm={deleteEquipmentTypeCallback}
        title={t('administration.equipment_type.delete_title')}
      >
        <Typography>{t('administration.equipment_type.delete_text')}</Typography>
      </ConfirmationDialog>
      <CSVImportExportModal
        isOpen={csvModal.isOpen}
        onClose={csvModal.close}
        config={equipmentTypesConfig}
        loadConfig={loadEquipmentTypesConfig}
        companyId={companyId || ''}
        entityDisplayName={t('administration.equipment_type.title')}
        onImportComplete={csvModal.handleImportComplete}
      />
    </>
  );
});

export { EquipmentTypeManagement };
